import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import type { CmsBlock } from '../types/cms';

// ─── State ─────────────────────────────────────────────────────────────────────

interface EditModeState {
  isEditMode: boolean;
  selectedBlockId: string | null;
  blocks: CmsBlock[];
}

type EditModeAction =
  | { type: 'SET_BLOCKS'; blocks: CmsBlock[] }
  | { type: 'UPDATE_BLOCK'; blockId: string; props: Record<string, unknown> }
  | { type: 'DELETE_BLOCK'; blockId: string }
  | { type: 'REORDER_BLOCKS'; orderedBlockIds: string[] }
  | { type: 'SELECT_BLOCK'; blockId: string | null };

/** Recursively find a block by ID in a tree of blocks with children */
function findBlockDeep(blocks: CmsBlock[], id: string): CmsBlock | undefined {
  for (const b of blocks) {
    if (b.id === id) return b;
    if (b.children?.length) {
      const found = findBlockDeep(b.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

/** Recursively map blocks (including children) */
function mapBlocksDeep(blocks: CmsBlock[], fn: (b: CmsBlock) => CmsBlock | null): CmsBlock[] {
  return blocks
    .map((b) => {
      const mapped = fn(b);
      if (!mapped) return null;
      if (mapped.children?.length) {
        return { ...mapped, children: mapBlocksDeep(mapped.children, fn) };
      }
      return mapped;
    })
    .filter(Boolean) as CmsBlock[];
}

function reducer(state: EditModeState, action: EditModeAction): EditModeState {
  switch (action.type) {
    case 'SET_BLOCKS':
      return { ...state, blocks: action.blocks };
    case 'UPDATE_BLOCK':
      return {
        ...state,
        blocks: mapBlocksDeep(state.blocks, (b) =>
          b.id === action.blockId ? { ...b, props: action.props } : b,
        ),
      };
    case 'DELETE_BLOCK':
      return {
        ...state,
        blocks: mapBlocksDeep(state.blocks, (b) =>
          b.id === action.blockId ? null : b,
        ),
      };
    case 'REORDER_BLOCKS': {
      const ordered = action.orderedBlockIds
        .map((id) => state.blocks.find((b) => b.id === id))
        .filter(Boolean) as CmsBlock[];
      return { ...state, blocks: ordered.map((b, i) => ({ ...b, sortOrder: i })) };
    }
    case 'SELECT_BLOCK':
      return { ...state, selectedBlockId: action.blockId };
    default:
      return state;
  }
}

// ─── Context ───────────────────────────────────────────────────────────────────

interface EditModeContextValue extends EditModeState {
  dispatch: React.Dispatch<EditModeAction>;
  getBlockProps: (blockId: string) => Record<string, unknown> | undefined;
}

const EditModeContext = createContext<EditModeContextValue>({
  isEditMode: false,
  selectedBlockId: null,
  blocks: [],
  dispatch: () => undefined,
  getBlockProps: () => undefined,
});

export function useEditMode() {
  return useContext(EditModeContext);
}

// ─── Provider ──────────────────────────────────────────────────────────────────

export function EditModeProvider({ children }: { children: React.ReactNode }) {
  const isEditMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('editMode') === 'true';

  const [state, dispatch] = useReducer(reducer, {
    isEditMode,
    selectedBlockId: null,
    blocks: [],
  });

  const getBlockProps = useCallback(
    (blockId: string) => state.blocks.find((b) => b.id === blockId)?.props,
    [state.blocks],
  );

  // When a block is selected from the canvas, notify the parent window
  useEffect(() => {
    if (!isEditMode || !state.selectedBlockId) return;
    const block = findBlockDeep(state.blocks, state.selectedBlockId);
    if (!block) return;
    window.parent.postMessage(
      {
        type: 'BLOCK_SELECT',
        blockId: block.id,
        blockType: block.type,
        blockDefId: block.blockDefinitionId ?? null,
        props: block.props,
      },
      '*',
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedBlockId]);

  return (
    <EditModeContext.Provider value={{ ...state, dispatch, getBlockProps }}>
      {children}
    </EditModeContext.Provider>
  );
}
